import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { Wallet, TrendingUp, Folder, FileCheck, Plus, Flame } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RotatingBanners from "../components/RotatingBanners";

export default function Dashboard() {
  const { user } = useAuth();
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user && user.account_type === "real_estate") return <RealEstateDashboard user={user} />;
  return <ArchitectDashboard user={user} />;
}

function pickRandom(arr, n) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

function ArchitectDashboard({ user }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";
  const isTrader = user?.account_type === "trader";
  const isStudioOrRE = user?.account_type === "studio" || user?.account_type === "real_estate";
  const VAT_RATE = 0.22;
  // Solo i Trader vedono prezzi scorporati IVA. Studio + Real Estate vedono
  // il prezzo lordo (IVA + trasporto + montaggio inclusi) — quello che il
  // cliente paga effettivamente. Allineato con la logica di Outlet.jsx.
  const stripVat = (amount) => Math.ceil(Number(amount || 0) / (1 + VAT_RATE));
  const stripVatIfTrader = (amount) => isTrader ? stripVat(amount) : Math.ceil(Number(amount || 0));
  const disclaimerKey = isStudioOrRE ? "vat_included_full" : "vat_excluded";
  const [wallet, setWallet] = useState({ balance: 0, transactions: [], is_redeemable: false, redeemable_amount: 0, redeemable_cap: 0, closed_total: 0 });
  const [projects, setProjects] = useState([]);
  const [banners, setBanners] = useState([]);
  const [outletPicks, setOutletPicks] = useState([]);

  useEffect(() => {
    if (!isTrader) {
      api.get("/rewards/wallet").then(r => setWallet(r.data)).catch(() => {});
    }
    api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
    api.get("/banners").then(r => setBanners(r.data)).catch(() => {});
    api.get("/flash-sales").then(r => setOutletPicks(pickRandom(r.data, 3))).catch(() => {});
  }, [isTrader]);

  const active = projects.filter(p => p.status !== "abbandonato" && p.status !== "chiuso");
  const valorized = projects.filter(p => p.status === "valorizzato" || p.status === "chiuso");
  const totalValue = projects.reduce((s, p) => s + (p.value || 0), 0);
  const firstName = user?.name?.split(" ")[0] || (isTrader ? "Trader" : "Studio");

  return (
    <div className="pt-28 pb-20 bg-cream2 min-h-[100svh]" data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("dashboard.section")}</div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light text-ink">{t("dashboard.hi", { name: firstName })}</h1>
            <p className="mt-2 text-slate1">{user?.studio}</p>
          </div>
          <Link to="/progetti" className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift self-start md:self-auto" data-testid="dash-new-project">
            <Plus size={14} /> {t("dashboard.new_project")}
          </Link>
        </div>

        <div className={`mt-10 grid grid-cols-1 ${isTrader ? "md:grid-cols-3" : "md:grid-cols-4"} gap-6`}>
          {!isTrader && (
            <div className="bg-ink text-cream p-8 rounded-sm" data-testid="kpi-wallet">
              <div className="flex items-center justify-between">
                <div className="text-[11px] tracking-[0.3em] uppercase text-cream/60">{t("dashboard.wallet")}</div>
                <Wallet size={18} className="text-cream/80" />
              </div>
              <div className="mt-6 font-display text-5xl">€ {wallet.balance.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
              <div className="mt-1 text-sm text-cream/60">{t("dashboard.credit_cumulated")}</div>
              {wallet.is_redeemable ? (
                <div className="mt-5 p-3 border border-gold/40 bg-gold/10 rounded-sm" data-testid="redeemable-badge">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gold">{t("dashboard.redeemable_now")}</div>
                  <div className="font-display text-2xl text-gold mt-1">€ {wallet.redeemable_amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                  <div className="text-[10px] text-cream/50 mt-1">{t("dashboard.closed_of", { closed: wallet.closed_total.toLocaleString("it-IT") })}</div>
                </div>
              ) : wallet.balance > 0 && (
                <div className="mt-5 p-3 border border-cream/15 rounded-sm text-[11px] text-cream/60" data-testid="redeemable-locked">
                  {t("dashboard.redeemable_locked_text", { threshold: (1000 / 0.05).toLocaleString("it-IT") })}
                  <br/>{t("dashboard.current_cap", { cap: wallet.redeemable_cap.toLocaleString("it-IT") })}
                </div>
              )}
            </div>
          )}
          <div className="bg-white border border-ink/10 p-8 rounded-sm" data-testid="kpi-projects">
            <div className="flex items-center justify-between">
              <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("dashboard.active_projects")}</div>
              <Folder size={18} className="text-slate1" />
            </div>
            <div className="mt-6 font-display text-5xl text-ink">{active.length}</div>
            <div className="mt-1 text-sm text-slate1">{t("dashboard.of_total", { total: projects.length })}</div>
          </div>
          <div className="bg-white border border-ink/10 p-8 rounded-sm" data-testid="kpi-valorized">
            <div className="flex items-center justify-between">
              <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("dashboard.valorized")}</div>
              <FileCheck size={18} className="text-slate1" />
            </div>
            <div className="mt-6 font-display text-5xl text-ink">{valorized.length}</div>
            <div className="mt-1 text-sm text-slate1">{t("dashboard.valorized_sub")}</div>
          </div>
          <div className="bg-white border border-ink/10 p-8 rounded-sm" data-testid="kpi-volume">
            <div className="flex items-center justify-between">
              <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("dashboard.total_volume")}</div>
              <TrendingUp size={18} className="text-slate1" />
            </div>
            <div className="mt-6 font-display text-4xl text-ink">€ {totalValue.toLocaleString("it-IT")}</div>
            <div className="mt-1 text-sm text-slate1">{t("dashboard.project_value")}</div>
          </div>
        </div>

        {banners.length > 0 && (
          <RotatingBanners banners={banners} className="mt-10" testid="dash-banners" />
        )}

        {outletPicks.length > 0 && (
          <div className="mt-12" data-testid="dash-outlet-picks">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold">
                  <Flame size={14} /> {t("dashboard.outlet_pick_pre")}
                </div>
                <div className="mt-2 font-display text-2xl text-ink">{t("dashboard.outlet_pick_title")}</div>
              </div>
              <Link to="/outlet" className="text-xs tracking-[0.2em] uppercase text-slate1 hover:text-ink" data-testid="dash-outlet-link">
                {t("dashboard.all_outlet")}
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {outletPicks.map((s) => {
                const p = s.product || {};
                const listinoRaw = Number(p.price_retail || 0);
                const traderRaw = p.price_trader != null ? Number(p.price_trader) : null;
                const offerRaw = p.price_offer != null ? Number(p.price_offer) : null;
                const mainRaw = isTrader && traderRaw != null ? traderRaw : (offerRaw != null ? offerRaw : listinoRaw);
                const originalRaw = (isTrader && traderRaw != null) || offerRaw != null ? listinoRaw : null;
                // price_trader è salvato GIÀ scorporato IVA (offerta / 1.22, ceil)
                const main = isTrader && traderRaw != null ? Math.ceil(Number(traderRaw)) : stripVatIfTrader(mainRaw);
                const original = originalRaw != null ? stripVatIfTrader(originalRaw) : null;
                const discountPct = original && original > 0 && main < original
                  ? Math.round((1 - main / original) * 100)
                  : 0;
                const cover = (p.images && p.images.length > 0)
                  ? `${process.env.REACT_APP_BACKEND_URL}/api/products/images/${p.images[0].id}?v=${p.images[0].size || 0}`
                  : (p.image_url || "");
                return (
                  <Link
                    key={s.id}
                    to="/outlet"
                    className="bg-white border border-ink/10 rounded-sm overflow-hidden hover-lift block"
                    data-testid={`dash-outlet-${s.id}`}
                  >
                    <div className="relative overflow-hidden aspect-[4/3] bg-cream2">
                      {cover ? (
                        <img src={cover} alt={p.name} className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full bg-cream2 flex items-center justify-center text-slate1 text-xs">{t("dashboard.no_image")}</div>
                      )}
                      {discountPct > 0 && (
                        <div className="absolute top-3 left-3 px-3 py-1.5 bg-gold text-cream text-[11px] tracking-[0.2em] uppercase">
                          -{discountPct}%
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{p.brand}</div>
                      <div className="mt-1 font-display text-lg text-ink truncate">{p.name}</div>
                      <div className="mt-3 flex items-baseline gap-3">
                        <div className="font-display text-xl text-ink">€ {main.toLocaleString("it-IT")}</div>
                        {original != null && (
                          <div className="text-xs text-slate1 line-through">€ {original.toLocaleString("it-IT")}</div>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] text-slate1 italic">{t(`dashboard.${disclaimerKey}`)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12 bg-white border border-ink/10 rounded-sm" data-testid="dash-projects">
          <div className="flex items-center justify-between p-6 border-b border-ink/10">
            <div className="font-display text-xl text-ink">{t("dashboard.recent_projects")}</div>
            <Link to="/progetti" className="text-xs tracking-[0.2em] uppercase text-slate1 hover:text-ink">{t("dashboard.all_projects")}</Link>
          </div>
          {projects.length === 0 ? (
            <div className="p-10 text-center text-slate1">
              {t("dashboard.no_projects_yet")} <Link to="/progetti" className="text-ink underline">{t("dashboard.create_one")}</Link>.
            </div>
          ) : (
            <div className="divide-y divide-ink/10">
              {projects.slice(0, 8).map((p) => (
                <div key={p.id} className="p-6 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-ink font-medium">{p.name}</div>
                    <div className="text-sm text-slate1">{p.client_name || "—"} · {t("dashboard.state")}: {p.status.replace("_", " ")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl text-ink">{p.value > 0 ? `€ ${p.value.toLocaleString("it-IT")}` : "—"}</div>
                    {!isTrader && p.credit_earned > 0 && <div className="text-xs text-success">+ € {p.credit_earned.toFixed(2)} {t("dashboard.wallet_label").toLowerCase()}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!isTrader && wallet.transactions.length > 0 && (
          <div className="mt-8 bg-white border border-ink/10 rounded-sm" data-testid="dash-wallet-tx">
            <div className="p-6 border-b border-ink/10 font-display text-xl text-ink">{t("dashboard.wallet_history")}</div>
            <div className="divide-y divide-ink/10">
              {wallet.transactions.slice(0, 10).map((tx) => (
                <div key={tx.id} className="p-6 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-ink">{tx.project_name || t("dashboard.credit_default")}</div>
                    <div className="text-xs text-slate1">{new Date(tx.created_at).toLocaleString(dateLocale)}</div>
                  </div>
                  <div className={`font-display text-lg ${tx.amount >= 0 ? "text-success" : "text-destructive"}`}>{tx.amount >= 0 ? "+" : ""} € {tx.amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RealEstateDashboard({ user }) {
  const { t } = useTranslation();
  const [wallet, setWallet] = useState({ balance: 0, transactions: [], confirmed_contacts: 0, pending_contacts: 0, is_redeemable: false, redeemable_amount: 0 });
  const [contacts, setContacts] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get("/rewards/wallet").then(r => setWallet(r.data)).catch(() => {});
    api.get("/contacts").then(r => setContacts(r.data)).catch(() => {});
    api.get("/banners").then(r => setBanners(r.data)).catch(() => {});
  }, []);

  const recent = contacts.slice(0, 5);
  const firstName = user?.name?.split(" ")[0] || "Real Estate";

  return (
    <div className="pt-28 pb-20 bg-cream2 min-h-[100svh]" data-testid="dashboard-real-estate">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("dashboard.section_re")}</div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light text-ink">{t("dashboard.hi", { name: firstName })}</h1>
            <p className="mt-3 text-slate1 max-w-2xl">
              <Trans i18nKey="dashboard.re_intro" />
            </p>
          </div>
          <Link to="/contatti" className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift" data-testid="re-dashboard-go-contatti">
            <Plus size={14} /> {t("dashboard.new_contact")}
          </Link>
        </div>

        <div className="mt-6 bg-rose-50 border-l-4 border-rose-600 rounded-sm p-4 lg:p-5" data-testid="re-dashboard-redflag">
          <div className="text-[10px] tracking-[0.25em] uppercase text-rose-700 font-semibold">{t("dashboard.re_redflag_title")}</div>
          <p className="mt-1.5 text-sm text-rose-900 leading-relaxed">
            <Trans i18nKey="dashboard.re_redflag_body" />
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-ink/10 p-6 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5"><Wallet size={12} /> {t("dashboard.re_wallet")}</div>
            <div className="mt-2 font-display text-3xl text-ink">€ {(wallet.balance || 0).toLocaleString("it-IT")}</div>
            <div className="text-[11px] text-slate1 mt-0.5">{wallet.is_redeemable ? t("dashboard.re_liquid_now") : t("dashboard.re_no_threshold")}</div>
          </div>
          <div className="bg-white border border-ink/10 p-6 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("dashboard.re_confirmed_contacts")}</div>
            <div className="mt-2 font-display text-3xl text-ink">{wallet.confirmed_contacts || 0}</div>
            <div className="text-[11px] text-slate1 mt-0.5">{t("dashboard.re_credited", { amount: ((wallet.confirmed_contacts || 0) * 100).toLocaleString("it-IT") })}</div>
          </div>
          <div className="bg-white border border-ink/10 p-6 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("dashboard.re_pending")}</div>
            <div className="mt-2 font-display text-3xl text-ink">{wallet.pending_contacts || 0}</div>
            <div className="text-[11px] text-slate1 mt-0.5">{t("dashboard.re_potential", { amount: ((wallet.pending_contacts || 0) * 100).toLocaleString("it-IT") })}</div>
          </div>
          <div className="bg-white border border-ink/10 p-6 rounded-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t("dashboard.re_total")}</div>
            <div className="mt-2 font-display text-3xl text-ink">{contacts.length}</div>
          </div>
        </div>

        {banners.length > 0 && (
          <div className="mt-12">
            <RotatingBanners banners={banners} variant="dashboard" testid="re-dashboard-banners" />
          </div>
        )}

        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-ink">{t("dashboard.re_latest")}</h2>
            <Link to="/contatti" className="text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink" data-testid="re-dashboard-view-all">{t("dashboard.re_view_all")}</Link>
          </div>
          {recent.length === 0 ? (
            <div className="bg-white border border-dashed border-ink/20 rounded-sm p-8 text-center text-sm text-slate1" data-testid="re-dashboard-empty">
              {t("dashboard.re_empty")}
              <div className="mt-3">
                <Link to="/contatti" className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm">
                  <Plus size={12} /> {t("dashboard.re_load_first")}
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-ink/10 rounded-sm divide-y divide-ink/10">
              {recent.map((c) => (
                <div key={c.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-slate1 truncate">{c.property_address} · {c.estimated_time_label}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm border ${
                      c.status === "confirmed" ? "bg-gold/10 border-gold/30 text-gold"
                      : c.status === "rejected" ? "bg-rose-50 border-rose-200 text-rose-700"
                      : "bg-cream2 border-ink/15 text-slate1"
                    }`}>
                      {c.status === "confirmed" ? t("dashboard.status_confirmed") : c.status === "rejected" ? t("dashboard.status_rejected") : t("dashboard.status_pending")}
                    </span>
                    <span className="font-display text-base text-ink">€{c.credit_amount}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
